'use client';

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, HelpCircle } from 'lucide-react';
import { QnAItem } from '@/lib/server-fetch';
import { PageHero, Mark, WRAP, inputClass } from '@/components/site/page-kit';

const categoryIcons: Record<string, string> = {
  Plywood: '🪵',
  Laminates: '🎨',
  Hardware: '🔩',
  Decoratives: '✨',
  Flooring: '🏠',
  'Buying Guide': '📋',
};

interface QnAContentProps {
  items: QnAItem[];
}

export function QnAContent({ items }: QnAContentProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(items.map((q) => q.category)))],
    [items]
  );

  const filtered = useMemo(() => {
    return items.filter((q) => {
      const matchesCategory = activeCategory === 'All' || q.category === activeCategory;
      const matchesSearch =
        !search ||
        q.question.toLowerCase().includes(search.toLowerCase()) ||
        q.answer.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [items, search, activeCategory]);

  return (
    <>
      <PageHero
        eyebrow="Knowledge Hub"
        title={<>Frequently Asked <Mark>Questions</Mark></>}
        description="Expert answers to help you choose the right materials for your interiors"
      >
        {/* Search */}
        <div className="relative mt-8 max-w-xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-hv-soft" strokeWidth={1.8} />
          <input
            type="text"
            placeholder="Search questions… e.g. 'best plywood for kitchen'"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${inputClass} h-14 pl-12`}
          />
        </div>
      </PageHero>

      {/* Category Filter */}
      <section className="sticky top-0 z-30 border-b border-hv-line bg-hv-paper">
        <div className={`${WRAP} py-3.5`}>
          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`h-10 whitespace-nowrap rounded-xl px-4 text-sm font-medium transition ${
                  activeCategory === cat
                    ? 'bg-hv-ink text-white'
                    : 'border border-hv-line bg-white text-hv-ink hover:border-hv-ink'
                }`}
              >
                {cat !== 'All' && categoryIcons[cat] ? `${categoryIcons[cat]} ` : ''}
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Q&A List */}
      <section className={`${WRAP} py-12 md:py-16`}>
        <div className="max-w-[960px]">
          <p className="mb-6 font-hv-mono text-[12px] uppercase tracking-[0.04em] text-hv-soft">
            Showing <span className="text-hv-ink">{filtered.length}</span> of {items.length} questions
            {activeCategory !== 'All' && (
              <span>
                {' '}in <span className="text-hv-orange">{activeCategory}</span>
              </span>
            )}
          </p>

          {filtered.length > 0 && (
            <div className="border-t-[1.5px] border-hv-ink">
              {filtered.map((item) => {
                const isOpen = openSlug === item.slug;
                return (
                  <div key={item.slug} className="border-b border-hv-line">
                    <button
                      onClick={() => setOpenSlug(isOpen ? null : item.slug)}
                      className="flex w-full items-start justify-between gap-4 py-4 text-left md:py-5"
                      aria-expanded={isOpen}
                      id={item.slug}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="mb-1.5 font-hv-mono text-[11px] uppercase tracking-[0.04em] text-hv-orange">
                          {item.category}
                        </div>
                        <p className="text-base font-medium leading-snug text-hv-ink md:text-lg">
                          {item.question}
                        </p>
                      </div>
                      <span
                        className={`mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-hv-line transition ${isOpen ? 'rotate-45' : ''}`}
                        aria-hidden="true"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                      </span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <p className="max-w-[760px] pb-5 pr-12 text-[15px] leading-relaxed text-hv-soft">
                            {item.answer}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}

          {filtered.length === 0 && (
            <div className="rounded-2xl border border-hv-line bg-white px-6 py-16 text-center">
              <HelpCircle className="mx-auto mb-4 h-10 w-10 text-hv-soft/50" strokeWidth={1.5} />
              <p className="text-lg text-hv-ink">No questions found</p>
              <p className="mt-1 text-sm text-hv-soft">
                Try a different search term or category
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
